import { useEffect, useMemo } from 'react';
import {
  FetchBuilderOptions,
  FetchFnArgs,
  HosApiOperatorSummary,
  ResApiOperatorSummary,
} from '../types/apiTypes';
import { useUser } from '../contexts/UserContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BASE_URL, BASE_URL_MAP } from '../lib/environment';
import { buildFetchParams } from '../lib/tsq-helpers';

const scope = 'Operators';

const fetchHosOperators = async (
  args: FetchFnArgs
): Promise<OperatorSummary[]> => {
  const params = buildFetchParams(args);
  const response = await fetch(
    `${BASE_URL_MAP['Hospitality']}/Operators`,
    params.options
  );

  const operators = await response.json();
  return operators.map(mapHospitalityOperator);
};

const fetchResOperators = async (
  args: FetchFnArgs
): Promise<OperatorSummary[]> => {
  const params = buildFetchParams(args);
  const response = await fetch(
    `${BASE_URL_MAP['Residential']}/Operators`,
    params.options
  );

  const operators = await response.json();
  return operators.map(mapResidentialOperator);
};

export const useHosOperators = () => {
  const queryClient = useQueryClient();
  const user = useUser();

  useEffect(() => {
    if (!user.loggedIn) queryClient.removeQueries([{ scope }]);
  }, [user.loggedIn]);

  return useQuery({
    queryKey: [{ scope, entity: 'Hospitality' }],
    queryFn: ({ signal }) =>
      fetchHosOperators({
        signal,
        access_token: user.token,
        baseUrl: BASE_URL,
      }),
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: user.permissions.canHos && user.loggedIn,
  });
};

export const useResOperators = () => {
  const queryClient = useQueryClient();
  const user = useUser();

  useEffect(() => {
    if (!user.loggedIn) queryClient.removeQueries([{ scope }]);
  }, [user.loggedIn]);

  return useQuery({
    queryKey: [{ scope, entity: 'Residential' }],
    queryFn: ({ signal }) =>
      fetchResOperators({
        signal,
        access_token: user.token,
        baseUrl: BASE_URL,
      }),
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: user.permissions.canRes && user.loggedIn,
  });
};

export const useOperators = () => {
  const residential = useResOperators();
  const hospitality = useHosOperators();

  const operators = useMemo(() => {
    if (hospitality.isFetching || residential.isFetching) return [];
    return [...(residential.data ?? []), ...(hospitality.data ?? [])];
  }, [
    residential.data,
    residential.isFetching,
    hospitality.data,
    hospitality.isFetching,
  ]);

  return {
    isLoading: residential.isFetching || hospitality.isFetching,
    data: operators,
  };
};

const mapHospitalityOperator = (operator: HosApiOperatorSummary) => ({
  id: operator.ID,
  name: operator.Name,
  code: operator.Code,
  type: 'Hospitality',
});

const mapResidentialOperator = (operator: ResApiOperatorSummary) => ({
  id: operator.ID,
  name: operator.Name,
  code: operator.Code,
  type: 'Residential',
});
